import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature, MetricLibraryGridType } from 'webapp/enums/enums';
import { copyObject, sortBy } from 'webapp/utils/common';

import eq from 'webapp/helpers/eq';
import didUpdate from 'webapp/modifiers/did-update';
import FilterLineProxyComponent from 'webapp/components/filter/line-proxy';
import FilterNoResultsComponent from 'webapp/components/filter/no-results';
import type { SectionBase } from 'webapp/components/metric-library/base';
import type { Args as GridBaseArgs } from 'webapp/components/metric-library/grid';
import MetricLibraryGridBlockDatasetComponent from 'webapp/components/metric-library/grid/block-dataset';
import MetricLibraryGridBlockDeprecationComponent from 'webapp/components/metric-library/grid/block-deprecation';
import MetricLibraryGridMetricsComponent from 'webapp/components/metric-library/grid/metrics';
import WizardSourceMetricStepsComponent from 'webapp/components/wizard/source-metric-steps';

import type { BatchSelect, Metric, Source, SourceType } from 'webapp/models-custom';
import FilterSortMetricLibrarySourceMetrics from 'webapp/models-custom/filter-sort/metric-library-source-metrics';

import type { AfterConnectFn, onConnectSourceResult } from 'webapp/services/connect';

export const enum Section {
	BASIC_METRICS = 'basic-metrics',
	CUSTOM_METRICS = 'custom-metrics',
}

type SectionExtendedBase = SectionBase & {
	type: MetricLibraryGridType;
};

const sectionOptions: SectionExtendedBase[] = [
	{
		id: Section.BASIC_METRICS,
		type: MetricLibraryGridType.BASIC,
		title: 'Prebuilt metrics',
		initialRowCount: 5,
	},
	{
		id: Section.CUSTOM_METRICS,
		type: MetricLibraryGridType.CUSTOM,
		title: 'Custom metrics',
		initialRowCount: 3,
	},
];

type SectionOption = GridBaseArgs & {
	type: MetricLibraryGridType;
	filterSort: FilterSortMetricLibrarySourceMetrics;
};

interface Args {
	sourceType: SourceType;
	batchSelect: BatchSelect<Metric>;
	metrics: Metric[];
	onSourceChange: (sourceId: string) => void;
	source?: Source | null;
}

export default class MetricLibrarySourceTabsMetricsComponent extends Component<Args> {
	@service('connect') declare connectService: Service.Connect;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;

	@tracked sections: SectionOption[] = [];

	mainFilterSort: FilterSortMetricLibrarySourceMetrics | null = null;
	allFilterSorts: {
		filterSort: FilterSortMetricLibrarySourceMetrics;
		rootElementSelector: string;
		preventBeforeFilter?: boolean;
	}[] = [];

	// Read per section in the template rather than mapped into new objects: `#each` keys by identity, so new objects
	// would remount every grid (and close an open metric info card) whenever a metric record changes.
	// Both sections stay in the DOM, the basic one hidden, because its filter sort's beforeFilter syncs the others.
	isSectionShown = (section: SectionOption): boolean =>
		(section.type === MetricLibraryGridType.BASIC && this.isBasicSectionShown) ||
		(section.type === MetricLibraryGridType.CUSTOM && this.isCustomSectionShown);

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = {
			[Section.BASIC_METRICS]: new FilterSortMetricLibrarySourceMetrics(
				{
					getAllItems: () => {
						return this.basicMetrics;
					},
					beforeFilter: () => {
						const newFiltersSelectedValues = filterSort[
							Section.BASIC_METRICS
						].filters.map((filter) => filter.selected);

						this.allFilterSorts.forEach((otherFilterSort) => {
							if (!otherFilterSort.preventBeforeFilter) {
								otherFilterSort.filterSort.filters.forEach((filter, index) => {
									// To prevent "You attempted to update `_selected` on `Filter`, but it had already been used previously in the same computation."
									next(() => {
										const updateSiblingFilter =
											!filter.isSort ||
											!filterSort[Section.BASIC_METRICS]
												.isSortBySearchPointsArray;

										if (updateSiblingFilter) {
											filter.selectOption(newFiltersSelectedValues[index]!);
										}
									});
								});

								otherFilterSort.filterSort.sortProp =
									filterSort[Section.BASIC_METRICS].sortProp;
								otherFilterSort.filterSort.typedFilter =
									filterSort[Section.BASIC_METRICS].typedFilter;
							}
						});
					},
				},
				this.filterPlaceholder,
			),
			[Section.CUSTOM_METRICS]: new FilterSortMetricLibrarySourceMetrics({
				getAllItems: () => {
					return this.customMetrics;
				},
			}),
		};

		sectionOptions.forEach((option, index) => {
			const isMainFilterSort = index === 0,
				optionFilterSort = filterSort[option.id as Section];

			this.allFilterSorts.push({
				filterSort: optionFilterSort,
				rootElementSelector: `.section.${option.id} ul`,
				preventBeforeFilter: isMainFilterSort,
			});

			if (isMainFilterSort) {
				this.mainFilterSort = optionFilterSort;
			}

			const section: SectionOption = {
				filterSort: optionFilterSort,
				classNames: option.id + ' lazy-grid',
				...copyObject(option),
			};

			this.sections.push(section);
		});
	}

	@cached
	get basicMetrics() {
		// not just basic, e.g. pushed for Custom tokens etc.
		return this.args.metrics.filter(
			(metric) =>
				!metric.isTypeBuilt &&
				!metric.isTypeMetricCalculationOrGeneric &&
				!metric.isTypeGoal,
		);
	}

	@cached
	get customQueryMetrics() {
		return this.args.metrics.filter((metric) => metric.isTypeBuilt);
	}

	@cached
	get calculatedMetrics() {
		return this.args.metrics.filter((metric) => metric.isTypeMetricCalculationOrGeneric);
	}

	@cached
	get customMetrics() {
		return sortBy(
			[...this.customQueryMetrics, ...this.calculatedMetrics],
			'usedCount',
		).reverse();
	}

	get isSearchInputEmpty(): boolean {
		return !this.mainFilterSort?.typedFilter;
	}

	get noSearchResults(): boolean {
		return (
			!this.isSearchInputEmpty &&
			this.allFilterSorts.every((filter) => filter.filterSort.noResults)
		);
	}

	get isBasicSectionShown() {
		if (this.isSourceDataset) {
			return false;
		}

		return !!(
			this.basicMetrics.length ||
			(this.args.source &&
				(this.args.sourceType.isToken || this.args.sourceType.isTokenWrapper))
		);
	}

	get isCustomSectionShown() {
		const hasCalculatedMetrics = this.calculatedMetrics.length > 0;
		const isHorizonSupported = this.args.sourceType.isHorizonSupported;

		if (
			this.args.sourceType.isSQLType &&
			!this.billingService.checkIfCan(BillingFeature.SQL_LEGACY_METRICS) &&
			!this.isSourceDataset &&
			!hasCalculatedMetrics &&
			!isHorizonSupported
		) {
			return false;
		}

		return (
			this.args.sourceType.isQBSupported ||
			!!this.isSourceDataset ||
			hasCalculatedMetrics ||
			isHorizonSupported
		);
	}

	get isDatasetSectionShown() {
		return this.args.sourceType.isDatasetSupported && !this.isSourceDataset;
	}

	get isLegacyCustomSectionShown() {
		return this.args.sourceType.isLegacyCustomSourceType && !this.isSourceDataset;
	}

	get filterPlaceholder() {
		return `Search ${this.args.sourceType.name} metrics...`;
	}

	get isEmptyTokenWrapper() {
		return (
			!this.args.source &&
			this.args.sourceType.isTokenWrapper &&
			this.args.metrics.length === 0
		);
	}

	get isSourceDataset() {
		return this.args.source?.isTypeOfDataset;
	}

	@action
	connectMetric() {
		void this.connectService
			.connectSourceFromIdOrKey(this.args.sourceType.key, { origin: 'allmetrics' })
			.then((result) => {
				if (result.isSuccess) {
					this.args.onSourceChange(String(result.source.id));
				}
			});
	}

	@action
	connectTokenWrapper(_sourceKey: string, afterConnect: AfterConnectFn) {
		const sourceType = this.args.sourceType;

		void sourceType
			.connectNew({ origin: `${sourceType.key}token` })
			.then((data: onConnectSourceResult) => {
				if (typeof afterConnect === 'function') {
					afterConnect(data.isSuccess, data.isSuccess ? data.source : undefined);
				}
			});
	}

	@action
	createDataboard() {
		if (this.billingService.checkIfCan(BillingFeature.BOARDS, true)) {
			this.navigationService.transitionToRoute('designer');
		}
	}

	@action
	onOutsideSourceTypeChange() {
		if (this.mainFilterSort) {
			this.mainFilterSort.searchPlaceholder = this.filterPlaceholder;
		}
	}
	<template>
		<div
			class="metric-library-source-metrics"
			{{didUpdate this.onOutsideSourceTypeChange @sourceType}}
		>
			{{#if this.isEmptyTokenWrapper}}
				<WizardSourceMetricStepsComponent
					@sourceType={{@sourceType}}
					@onConnectSource={{this.connectTokenWrapper}}
					@onCreateNewDataboard={{this.createDataboard}}
				/>
			{{else}}
				<div class="filter-actions">
					{{#if this.mainFilterSort}}
						<FilterLineProxyComponent @model={{this.mainFilterSort}} />

						{{#if this.noSearchResults}}
							<FilterNoResultsComponent @model={{this.mainFilterSort}} />
						{{/if}}
					{{/if}}
				</div>
				<div class="ml-sections">
					{{#each this.sections as |section index|}}
						{{#if this.isLegacyCustomSectionShown}}
							{{#if (eq index 0)}}
								<MetricLibraryGridBlockDeprecationComponent
									@sourceType={{@sourceType}}
								/>
							{{/if}}
						{{/if}}
						{{#if this.isDatasetSectionShown}}
							{{#if (eq index 1)}}
								<MetricLibraryGridBlockDatasetComponent
									@sourceType={{@sourceType}}
									@source={{@source}}
								/>
							{{/if}}
						{{/if}}
						<MetricLibraryGridMetricsComponent
							@isShown={{this.isSectionShown section}}
							@type={{section.type}}
							@source={{@source}}
							@sourceType={{@sourceType}}
							@filterSort={{section.filterSort}}
							@title={{section.title}}
							@initialRowCount={{section.initialRowCount}}
							@classNames={{section.classNames}}
							@batchSelect={{@batchSelect}}
							@onConnectMetric={{this.connectMetric}}
						/>
					{{/each}}
				</div>
			{{/if}}
		</div>
	</template>
}
